import type { Column } from '@devexpress/dx-react-grid';
import styles from './SpreadsheetPreviewTable.module.css';

interface SpreadsheetPreviewTableProps {
  readonly columns: Column[];
  readonly rows: Record<string, string>[];
}

/** Read-only spreadsheet renderer for the file-preview slide-out.
 *
 *  Deliberately a plain `<table>` rather than the shared `DataGrid`: that
 *  component forces `minWidth: auto !important` on its inner table, so wide
 *  sheets squeeze their columns to fit instead of overflowing, and it's used
 *  by ~30 other listings that must not inherit preview-specific styling.
 *  Here the table sizes to its content (`width: max-content`) inside a
 *  horizontally scrollable wrapper, so many columns produce a scrollbar
 *  instead of unreadable slivers. */
export default function SpreadsheetPreviewTable({
  columns,
  rows,
}: SpreadsheetPreviewTableProps) {
  return (
    <div className={styles.scroller}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.name} className={styles.th} scope="col">
                {column.title ?? column.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Parsed sheet rows have no natural id and never reorder, so the
            // row index is a stable key for this render-only table.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex} className={styles.tr}>
              {columns.map((column) => (
                <td key={column.name} className={styles.td}>
                  {row[column.name] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
